/**
 * Utilitaire d'export universel Excel (.xlsx) & CSV
 * Compatible avec Microsoft Excel (Windows & Mac), LibreOffice, Google Sheets
 * Génère des classeurs Excel natifs (.xlsx) avec SheetJS
 */
import * as XLSX from 'xlsx';

export interface ExportColumn<T = any> {
  header: string;
  key?: keyof T | string;
  formatter?: (value: any, item: T) => string | number | null | undefined;
}

/**
 * Nettoie une valeur pour l'export Excel (conserve les nombres, chaîne propre, ou vide)
 */
const cleanValue = (val: any): any => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number') return val;
  if (typeof val === 'boolean') return val ? 'Oui' : 'Non';
  const str = String(val).trim().replace(/\r?\n|\r/g, ' ');
  return str;
};

/**
 * Génère et télécharge le fichier Excel .xlsx natif
 */
const generateAndDownloadExcel = (
  headerRow: string[],
  dataRows: any[][],
  filename: string
) => {
  // Construire la matrice de données (en-tête + lignes)
  const aoaData = [headerRow, ...dataRows];

  // Créer la feuille de calcul
  const worksheet = XLSX.utils.aoa_to_sheet(aoaData);

  // Calcul dynamique de la largeur des colonnes pour un affichage optimal dans Excel
  const colWidths = headerRow.map((colHeader, colIdx) => {
    let maxLen = String(colHeader || '').length;
    for (let r = 0; r < dataRows.length; r++) {
      const cellVal = dataRows[r][colIdx];
      if (cellVal !== undefined && cellVal !== null) {
        const len = String(cellVal).length;
        if (len > maxLen) maxLen = len;
      }
    }
    // Largeur minimale 10, maximale 60 caractères
    return { wch: Math.min(Math.max(maxLen + 3, 10), 60) };
  });
  worksheet['!cols'] = colWidths;

  // Créer le classeur
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Données');

  // Nettoyer et formater le nom de fichier .xlsx
  let cleanFilename = filename.trim();
  if (cleanFilename.toLowerCase().endsWith('.csv')) {
    cleanFilename = cleanFilename.slice(0, -4) + '.xlsx';
  } else if (!cleanFilename.toLowerCase().endsWith('.xlsx')) {
    const today = new Date().toISOString().slice(0, 10);
    cleanFilename = `${cleanFilename}_${today}.xlsx`;
  }

  // Écrire le binaire XLSX et déclencher le téléchargement via Blob
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', cleanFilename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exporte des données vers un fichier Excel natif (.xlsx)
 * Supporte 3 signatures courantes pour une rétro-compatibilité totale :
 * 1. exportToExcel(filename, columns, data)
 * 2. exportToExcel(data, headers, filename)
 * 3. exportToExcel(data, filename)
 */
export function exportToExcel<T = any>(filename: string, columns: ExportColumn<T>[], data: T[]): void;
export function exportToExcel<T = any>(data: T[], headers: { header: string; key: string }[], filename: string): void;
export function exportToExcel<T = any>(data: T[], filename: string): void;
export function exportToExcel(...args: any[]): void {
  // Signature 1: exportToExcel(filename: string, columns: ExportColumn[], data: any[])
  if (typeof args[0] === 'string' && Array.isArray(args[1]) && Array.isArray(args[2])) {
    const filename: string = args[0];
    const columns: ExportColumn[] = args[1];
    const data: any[] = args[2];

    if (!data || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    const headerRow = columns.map(col => col.header);
    const rows = data.map(item => {
      return columns.map(col => {
        let rawValue: any;
        if (col.formatter) {
          rawValue = col.formatter(col.key ? (item as any)[col.key] : undefined, item);
        } else if (col.key) {
          rawValue = (item as any)[col.key];
        }
        return cleanValue(rawValue);
      });
    });

    generateAndDownloadExcel(headerRow, rows, filename);
    return;
  }

  // Signature 2: exportToExcel(data: any[], headers: { header: string, key: string }[], filename: string)
  if (Array.isArray(args[0]) && Array.isArray(args[1]) && typeof args[2] === 'string') {
    const data: any[] = args[0];
    const headers: { header: string; key: string }[] = args[1];
    const filename: string = args[2];

    if (!data || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    const headerRow = headers.map(h => h.header);
    const rows = data.map(item => {
      return headers.map(h => {
        const rawValue = (item as any)[h.key];
        return cleanValue(rawValue);
      });
    });

    generateAndDownloadExcel(headerRow, rows, filename);
    return;
  }

  // Signature 3: exportToExcel(data: any[], filename: string)
  if (Array.isArray(args[0]) && typeof args[1] === 'string') {
    const data: any[] = args[0];
    const filename: string = args[1];

    if (!data || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    const keys = Object.keys(data[0]);
    const headerRow = keys;
    const rows = data.map(item => {
      return keys.map(k => {
        const rawValue = (item as any)[k];
        return cleanValue(rawValue);
      });
    });

    generateAndDownloadExcel(headerRow, rows, filename);
    return;
  }

  throw new Error('Arguments invalides fournis à exportToExcel');
}

/**
 * Alias de rétro-compatibilité pour exportToCSV
 * Redirige automatiquement vers la génération Excel (.xlsx) demandée
 */
export const exportToCSV = exportToExcel;

export default exportToExcel;
